/*
 * Copyright (c) 2025 Bolmate B.V.
 */
import './style/splitVat.scss';

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {
    AwesomeIcon,
    ChangeFunction,
    CloseButton,
    ElementPosition,
    Link,
    SelectItemShape,
    WithBackground
} from '@dvrd/dvr-controls';

import {BolmateButton, BolmateNumberInput, BolmateSelect} from '../../controls';

import {SplitVATData} from './splitVATController';

interface Props {
    onChange: (index: number) => ChangeFunction<string>;
    onChangePercentage: (index: number) => ChangeFunction<string>;
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onAddVat: MouseEventHandler;
    onDeleteVat: (index: number) => MouseEventHandler;
    onClickExplanation: MouseEventHandler;
    active: boolean;
    vats: SplitVATData[];
}

export default function SplitVAT(props: Props) {
    const {t} = useTranslation();
    const {active, vats, onAddVat, onDeleteVat, onSubmit, onChange, onClose, onChangePercentage, onClickExplanation} =
        props;
    const percentageItems: SelectItemShape[] = [
        {value: '-1', label: t('vat.split.choose', 'Kiezen...')},
        {value: '6', label: '6%'},
        {value: '9', label: '9%'},
        {value: '12', label: '12%'},
        {value: '19', label: '19%'},
        {value: '21', label: '21%'}
    ];

    function getPercentageItems(index: number): SelectItemShape[] {
        const usedRates: Set<string> = new Set(
            vats
                .filter((vatData: SplitVATData, idx: number) => idx !== index)
                .map((vatData: SplitVATData) => vatData.percentage)
        );
        return percentageItems.filter(
            (item: SelectItemShape) => item.value === '-1' || !usedRates.has(item.value as string)
        );
    }

    function renderVats() {
        return vats.map((vatData: SplitVATData, index: number) => (
            <div className='row' key={index}>
                <BolmateSelect
                    value={vatData.percentage}
                    onChange={onChangePercentage(index)}
                    items={getPercentageItems(index)}
                    label={t('vat.split.percentageLabel', 'BTW percentage')}
                />
                <BolmateNumberInput
                    onChange={onChange(index)}
                    value={vatData.value}
                    label={t('vat.split.amountPerUnitLabel', 'BTW bedrag per stuk')}
                    ornaments={{
                        element: <AwesomeIcon name='euro-sign' />,
                        placement: ElementPosition.LEFT
                    }}
                />
                <AwesomeIcon name='trash-alt' className='trash-icon' onClick={onDeleteVat(index)} />
            </div>
        ));
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='split-vat default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('vat.split.title', 'Samengestelde BTW')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                <div className='body-container'>
                    <Link className='explanation-label' onClick={onClickExplanation}>
                        {t('vat.split.whatIs', 'Wat is samengestelde BTW?')}
                    </Link>
                    {renderVats()}
                    {vats.length < 5 && (
                        <label onClick={onAddVat} className='add-vat-label'>
                            <AwesomeIcon name='plus' /> {t('vat.split.addRate', 'tarief toevoegen')}
                        </label>
                    )}
                    <div className='disclaimer-row'>
                        <label className='disclaimer'>
                            {t('vat.split.disclaimer', 'Het gebruik van samengestelde BTW is op eigen risico.')}
                        </label>
                        <div className='disclaimer-info-container'>
                            <AwesomeIcon name='info-circle' className='info-icon' />
                            <p className='disclaimer-info'>
                                {t(
                                    'vat.split.disclaimerInfo',
                                    'Wij ontvangen van Bol geen informatie over BTW tarieven en kunnen daarom geen automatische berekeningen en wijzigingen toepassen op samengestelde BTW bedragen. Bij prijswijzigingen dienen de samengestelde BTW bedragen handmatig aangepast te worden.'
                                )}
                            </p>
                        </div>
                    </div>
                </div>
                <div className='footer-container'>
                    <BolmateButton onClick={onClose} label={t('vat.split.cancel', 'Annuleren')} secondary />
                    <BolmateButton onClick={onSubmit} label={t('vat.split.apply', 'Toepassen')} />
                </div>
            </div>
        </WithBackground>
    );
}
